import { type ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

import { INVITATION_ACCESS_TOKEN_QUERY, INVITATION_PARTNER_QUERY } from '../../pages/PartnerInvitation';
import {
    type ConnectionType,
    ConnectionTypes,
    type EmailInvitation,
    type Invitation,
    type Partner,
} from '../../services/partnerNetwork.types';
import CreateStepOne from './CreateStepOne';
import CreateStepTwo from './CreateStepTwo';
import {
    useCreateEmailInvitationMutation,
    useCreateInvitationMutation,
    useStorePartnerMutation,
} from '../../services/partnerNetworkApi';
import { useAppSelector } from '../../configuration/setup/hooks';
import { getInvitationDialogPartner } from '../../layout/appSlice';
import { config } from '../../config';
import { useRestrictConnection } from '../../routes/routeHooks';
import FooterStepOne from './FooterStepOne';
import FooterStepTwo from './FooterStepTwo';
import { getUserProfile } from '../../configuration/login/loginSlice';

export type FormEmailData = {
    name: string;
    emailAddress: string;
};

type CreatePartnerDialogProps = {
    show: boolean;
    onClose: () => void;
    disableConnectionTypeRadioButton?: boolean;
};

const buildInvitationLink = (baseUrl: string, invitation: Invitation): string => {
    const partnerId = invitation.partnerId;
    const accessToken = invitation.id;
    const path = '/#/invitation?';
    const partnerQuery = `${INVITATION_PARTNER_QUERY}=${partnerId}`;
    const accessTokenQuery = `${INVITATION_ACCESS_TOKEN_QUERY}=${accessToken}`;

    return `${baseUrl}${path}${partnerQuery}&${accessTokenQuery}`;
};

const mapToEmailInvitation = (
    invitation: Invitation,
    formEmailData: FormEmailData,
    locale: string
): EmailInvitation => ({
    partnerId: invitation.partnerId,
    invitationId: invitation.id,
    emailAddress: formEmailData.emailAddress,
    inviteeName: formEmailData.name,
    language: locale,
});

const CreatePartnerDialog = (props: CreatePartnerDialogProps) => {
    const { show, onClose } = props;

    const intl = useIntl();
    const clipboard = useClipboard();

    const invitationDialogPartner = useAppSelector(getInvitationDialogPartner);

    const [updatePartner] = useStorePartnerMutation();
    const [createInvitation] = useCreateInvitationMutation();
    const [createEmailInvitation] = useCreateEmailInvitationMutation();

    const [partnerName, setPartnerName] = useState<string | undefined>();

    const [partnerDescription, setPartnerDescription] = useState<string | undefined>();
    const [partner, setPartner] = useState<Partner | undefined>();
    const [invitation, setInvitation] = useState<Invitation | undefined>();
    const [invitationLink, setInvitationLink] = useState<string>();

    const defaultConnectionType = ConnectionTypes.ORDER_EXCHANGE;
    const [connectionType, setConnectionType] = useState<ConnectionType>(defaultConnectionType);

    const locale = useAppSelector(getUserProfile)?.locale ?? intl.locale;

    const restrictConnectionType = useRestrictConnection();
    useEffect(() => {
        setConnectionType(restrictConnectionType ?? defaultConnectionType);
    }, [restrictConnectionType]);

    const emailForm = useForm<FormEmailData>({
        mode: 'onSubmit',
    });

    const onSubmit = async (formEmailData: FormEmailData) => {
        if (!invitation) {
            return;
        }
        const emailInvitation = mapToEmailInvitation(invitation, formEmailData, locale);
        createEmailInvitation(emailInvitation);
        handleClose();
    };

    const createNewInvitationLink = async () => {
        if (!partner) {
            return;
        }

        const redirectUri = config.partnerNetworkOverviewPageUrl;

        if (!redirectUri) {
            console.error('Redirect URI is not defined!');
        } else {
            const result = await createInvitation({ partnerId: partner.id, invitationRedirectUri: redirectUri });
            if ('data' in result && result.data) {
                setInvitation(result.data);
            }
        }
    };

    useEffect(() => {
        setPartner(invitationDialogPartner);
    }, [invitationDialogPartner]);

    useEffect(() => {
        const createLink = async () => {
            try {
                await createNewInvitationLink();
            } catch (err) {
                console.error('Failed to create invitation link', err);
            }
        };

        createLink();
    }, [partner]);

    useEffect(() => {
        if (clipboard.copied) {
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:network.addPartner.invitationLinkCopied',
                })
            );
        }
    }, [clipboard.copied]);

    useEffect(() => {
        if (invitation) {
            setInvitationLink(buildInvitationLink(window.location.origin, invitation));
        }
    }, [invitation]);

    const reset = () => {
        setPartnerName(undefined);
        setPartnerDescription(undefined);
        setPartner(undefined);
        setInvitation(undefined);
        emailForm.reset();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleCreatePartner = async () => {
        const newPartner = {
            id: uuid(),
            name: partnerName,
            description: partnerDescription,
            connectionType: connectionType,
            status: 'pending',
        } as Partner;
        await updatePartner(newPartner);

        setPartner(newPartner);
    };

    const handleChangePartnerDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setPartnerDescription(event.target.value);
    };

    const isFirstStep = !partner;
    const hasInvitation = !!invitationLink;

    const title = (
        <FormattedMessage
            id={isFirstStep ? 'intl-msg:network.addPartner.step1.title' : 'intl-msg:network.addPartner.step2.title'}
        />
    );

    const renderFooter = () => {
        if (isFirstStep) {
            return (
                <FooterStepOne
                    partnerName={partnerName}
                    handleClose={handleClose}
                    handleCreatePartner={handleCreatePartner}
                />
            );
        }

        if (hasInvitation) {
            return (
                <FooterStepTwo
                    onCopyInvitationClick={() => clipboard.copy(invitationLink)}
                    onEmailInvitationClicked={emailForm.handleSubmit(onSubmit)}
                />
            );
        }

        return null;
    };

    const renderBody = () => {
        if (isFirstStep) {
            return (
                <CreateStepOne
                    partnerName={partnerName}
                    onChangePartnerName={setPartnerName}
                    partnerDescription={partnerDescription}
                    onChangePartnerDescription={handleChangePartnerDescription}
                    connectionType={connectionType}
                    setConnectionType={setConnectionType}
                    restrictConnectionType={restrictConnectionType}
                    disableRadioButton={props.disableConnectionTypeRadioButton ?? true}
                />
            );
        }
        return <CreateStepTwo form={emailForm} connectionType={connectionType} />;
    };

    return (
        <Dialog
            show={show}
            title={title}
            footer={renderFooter()}
            body={renderBody()}
            bsSize={Dialog.SIZE_SM}
            onClose={handleClose}
            showCloseButton
        />
    );
};

export default CreatePartnerDialog;
